import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Modal, Button } from 'reactstrap'
import DatetimeRenderer from "../../Material/DatetimeRenderer"
import toTitleCase from "../../Functions/toTitleCase"
import DocumentViewer from "../../Incidents/DocumentsTab/IncidentDocumentModal/DocumentViewer"
import ModalSkeleton from "../../Incidents/DocumentsTab/IncidentDocumentModal/ModalSkeleton"
import PanelResizer, { DEFAULT_PANEL_WIDTH } from "../../Incidents/DocumentsTab/IncidentDocumentModal/PanelResizer"
import { InfoRow } from "../../Incidents/DocumentsTab/IncidentDocumentModal/DocumentInfoPanels"
import { CarInspectionPictures } from "./Queries"
import "../../Incidents/DocumentsTab/IncidentDocumentModal/IncidentDocumentModal.css"
import "./CarInspections.css"

/**
 * Fullscreen viewer for an inspection's pictures, laid out like the incident document
 * viewer (IncidentDocumentModal) and built from its pieces. Read-only: car pictures have
 * no edit or archive mutation. Pictures never open outside the app -- no download
 * button, and the viewer's "open it instead" fallback link is switched off.
 */
export default function CarInspectionPicturesModal({ carConditionId, inspection, carLabel, open, handleClose }) {
    const { data, loading, error } = useQuery(CarInspectionPictures, {
        variables: { carConditionId },
        fetchPolicy: "cache-and-network",
    })
    const [index, setIndex] = useState(0)
    const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH)
    const panelRef = useRef(null)

    const pictures = data?.carCondition?.carpicturesSet?.edges?.map(edge => edge.node) || []
    const picture = pictures[index]
    const hasPrev = index > 0
    const hasNext = index < pictures.length - 1
    const formType = inspection?.formType ? toTitleCase(inspection.formType) : null

    // Arrow keys step through the pictures while the modal is open.
    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "ArrowLeft") setIndex(current => Math.max(current - 1, 0))
            if (event.key === "ArrowRight") setIndex(current => Math.min(current + 1, pictures.length - 1))
        }
        window.addEventListener("keydown", onKeyDown)
        return () => window.removeEventListener("keydown", onKeyDown)
    }, [pictures.length])

    // The panel width goes to CSS as a custom property, as in the incident viewer. Depends on
    // `picture` too: the panel does not exist until the pictures have loaded.
    useEffect(() => {
        if (panelRef.current) panelRef.current.style.setProperty("--idm-panel-width", `${panelWidth}px`)
    }, [panelWidth, picture])

    return (
        <Modal className="idm-modal" isOpen={open} toggle={handleClose} fullscreen>
            <div className="idm-shell">
                {loading && !data && <ModalSkeleton />}
                {error && <div className="idm-load-error">Couldn&rsquo;t load pictures: {error.message}</div>}
                {!loading && !error && !picture && <div className="idm-load-error">No pictures were taken at this inspection.</div>}

                {!!picture && (
                    <>
                        <div className="idm-header">
                            <div className="idm-header-identity">
                                <span className="idm-file-icon">
                                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="5" width="18" height="14" rx="2" />
                                        <circle cx="9" cy="10" r="1.6" /><path d="M21 16l-5-5-8 8" />
                                    </svg>
                                </span>
                                <div className="idm-header-text">
                                    <div className="idm-header-title-row">
                                        <span className="idm-header-title">{picture.pictureTypeDisplay || "Picture"}</span>
                                        {!!formType && <span className="idm-badge car-inspection-pictures-form-badge">{formType.toUpperCase()}</span>}
                                        {!!inspection?.cancellationReason && <span className="idm-badge idm-badge-archived">CANCELED</span>}
                                    </div>
                                    <span className="idm-header-meta">
                                        {[carLabel, inspection?.pk && `VGM-${inspection.pk}`].filter(Boolean).join(" · ")}
                                        {" · Taken "}{picture.dateAdded ? <DatetimeRenderer datetime={picture.dateAdded} /> : "--"}
                                    </span>
                                </div>
                            </div>
                            <div className="idm-header-actions">
                                <button type="button" className="idm-icon-btn" onClick={handleClose} title="Close" aria-label="Close">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="idm-body">
                            <DocumentViewer url={picture.pictureUrl} fileName={picture.pictureTypeDisplay} allowOpenOriginal={false} />

                            <PanelResizer width={panelWidth} onWidthChange={setPanelWidth} />

                            <div className="idm-side-panel" ref={panelRef}>
                                <div className="idm-tabs">
                                    <button type="button" className="idm-tab idm-tab-active">Details</button>
                                </div>
                                <div className="idm-panel-body idm-info-list">
                                    <InfoRow label="Picture type">{picture.pictureTypeDisplay || "--"}</InfoRow>
                                    <InfoRow label="Description">{picture.description || "--"}</InfoRow>
                                    <InfoRow label="Taken">{picture.dateAdded ? <DatetimeRenderer datetime={picture.dateAdded} /> : "--"}</InfoRow>
                                    <InfoRow label="Car">{carLabel || "--"}</InfoRow>
                                    <InfoRow label="Inspection">
                                        {[inspection?.pk && `VGM-${inspection.pk}`, formType].filter(Boolean).join(" · ") || "--"}
                                    </InfoRow>
                                    <InfoRow label="Picture">{index + 1} of {pictures.length}</InfoRow>

                                    <div className="car-inspection-pictures-thumbnails">
                                        {pictures.map((thumbnail, i) =>
                                            <img
                                                key={thumbnail.id}
                                                src={thumbnail.pictureUrl}
                                                alt={thumbnail.pictureTypeDisplay}
                                                title={thumbnail.pictureTypeDisplay}
                                                className={"car-inspection-pictures-thumbnail" + (i === index ? " active" : "")}
                                                onClick={() => setIndex(i)}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="idm-footer">
                            <div className="idm-footer-nav">
                                <Button className="idm-btn-secondary" disabled={!hasPrev} onClick={() => setIndex(index - 1)}>Prev</Button>
                                <span className="idm-footer-position">{index + 1} of {pictures.length}</span>
                                <Button className="idm-btn-secondary" disabled={!hasNext} onClick={() => setIndex(index + 1)}>Next</Button>
                            </div>
                            <div className="idm-footer-actions">
                                <Button className="idm-btn-close" onClick={handleClose}>Close</Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    )
}
